import { FormulaManager } from '@utils/managers/FormulaManager';
import type { IDocumentScheme } from '@models/document/IDocumentScheme';
import type { IFieldElem } from '@models/IFormData';
import { isDateValueType } from './evaluateFieldErrors';
import { collectValidatableControls, type ValidatableControl } from './schemeWalk';
import { ATTACHMENTS_FORM_PATH } from './attachmentsValidation';
import type { IDocumentTable } from '@models/Forms/IForms';

export const normalizeDocumentFieldPath = (path: string): string =>
    path.replace(/\.\[(\d+)\]/g, '.$1').replace(/\[(\d+)\]/g, '.$1');

const isNonEmptyRule = (rule: string | undefined): rule is string => Boolean(rule);

const getTableRules = (table: IDocumentTable): string[] =>
    [
        table.visibilityRules,
        ...(table.tableColumn ?? []).flatMap((column) => [column.visibilityRules, column.required]),
        ...(table.tableColumnDict ?? []).flatMap((column) => [column.visibilityRules, column.required]),
        ...(table.tableColumnAbook ?? []).flatMap((column) => [column.visibilityRules, column.required]),
        ...(table.tableColumnCalc ?? []).flatMap((column) => [column.visibilityRules, column.required]),
        ...(table.tableColumnAutoComplete ?? []).flatMap((column) => [column.visibilityRules, column.required]),
        ...(table.tables ?? []).flatMap(getTableRules),
    ].filter(isNonEmptyRule);

const getControlRules = (control: ValidatableControl): string[] => {
    if (control.kind === 'field') {
        return [
            ...control.visibilityChain,
            control.field.visibilityRules,
            control.field.required,
            control.field.maxLengthRules,
            ...(isDateValueType(control.field.valueType)
                ? [control.field.min, control.field.max, control.field.workCalendarRules]
                : []),
        ].filter(isNonEmptyRule);
    }

    if (control.kind === 'required') {
        return [...control.visibilityChain, control.control.visibilityRules, control.control.required].filter(
            isNonEmptyRule,
        );
    }

    if (control.kind === 'attachments') {
        return [...control.visibilityChain, control.table.required].filter(isNonEmptyRule);
    }

    return [...control.visibilityChain, ...getTableRules(control.table)].filter(isNonEmptyRule);
};

export interface IDocumentValidationIndex {
    controls: ValidatableControl[];
    controlsByPath: ReadonlyMap<string, readonly ValidatableControl[]>;
    paths: readonly string[];
    getDependents(path: string): readonly string[];
}

export const buildDocumentValidationIndex = (
    scheme: IDocumentScheme,
    fields: Record<string, IFieldElem>,
): IDocumentValidationIndex => {
    const controls = collectValidatableControls(scheme);
    const pathByKey = new Map<string, string>();
    const controlsByPath = new Map<string, ValidatableControl[]>();

    for (const control of controls) {
        // Блок вложений не имеет записи в fields: его значение лежит по фиксированному пути.
        const name = control.kind === 'attachments' ? ATTACHMENTS_FORM_PATH : fields[control.key]?.name;
        if (!name) continue;

        const path = normalizeDocumentFieldPath(name);
        pathByKey.set(control.key, path);
        const variants = controlsByPath.get(path);
        if (variants) variants.push(control);
        else controlsByPath.set(path, [control]);
    }

    const paths = Array.from(controlsByPath.keys());
    const knownPaths = new Set(paths);
    const directDependents = new Map<string, Set<string>>();
    const watchesByRule = new Map<string, readonly string[]>();

    const getWatchPaths = (rule: string): readonly string[] => {
        const cached = watchesByRule.get(rule);
        if (cached) return cached;

        const manager = new FormulaManager(rule);
        manager.Init(fields);
        // Дедуплицированный набор зависимостей (с field-backed параметрами удалённых функций),
        // а НЕ позиционный GetWatchFields — здесь важен граф зависимостей, не подстановка значений.
        const watches = manager
            .GetDependencyWatchFields()
            .filter((watch): watch is string => typeof watch === 'string' && watch.length > 0)
            .map(normalizeDocumentFieldPath);
        watchesByRule.set(rule, watches);
        return watches;
    };

    for (const control of controls) {
        const targetPath = pathByKey.get(control.key);
        if (!targetPath) continue;

        for (const rule of getControlRules(control)) {
            for (const sourcePath of getWatchPaths(rule)) {
                // Ссылка на путь вне валидируемых контролов (служебное/скрытое поле) НЕ делает
                // контрол зависимым от всех: такие пути не порождают blur-событий, а «глобальный
                // fallback» превращал deps каждого поля в почти полный список контролов схемы —
                // первый blur применял ошибки ко всей карточке (EUPDEV-12637, схема «Договор»,
                // где десятки правил ссылаются на служебные поля). Изменения таких путей
                // (каскадами) перепроверяются полным прогоном на submit — как в легаси.
                if (!knownPaths.has(sourcePath)) continue;
                if (sourcePath === targetPath) continue;

                let dependents = directDependents.get(sourcePath);
                if (!dependents) {
                    dependents = new Set<string>();
                    directDependents.set(sourcePath, dependents);
                }
                dependents.add(targetPath);
            }
        }
    }

    const dependentsCache = new Map<string, readonly string[]>();
    const getDependents = (rawPath: string): readonly string[] => {
        const path = normalizeDocumentFieldPath(rawPath);
        const cached = dependentsCache.get(path);
        if (cached) return cached;

        const collected = new Set<string>();
        const pending = [...(directDependents.get(path) ?? [])];
        while (pending.length > 0) {
            const dependent = pending.shift()!;
            if (dependent === path || collected.has(dependent)) continue;

            collected.add(dependent);
            pending.push(...(directDependents.get(dependent) ?? []));
        }

        collected.delete(path);
        const result = paths.filter((candidate) => collected.has(candidate));
        dependentsCache.set(path, result);
        return result;
    };

    return { controls, controlsByPath, paths, getDependents };
};
